import { useState, useEffect, useRef, Fragment, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { ArrowRight, Files, Trash2, Plus, ChevronDown, ChevronRight, Settings } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { formatCurrency } from '@/lib/budget-utils';
import type { SalesBudget, SalesCategory, ResourceBudget, RoleBudget } from '@/types/budget.types';
import type { GlobalCategory } from '@/types/category.types';
import { fetchGlobalCategories } from '@/lib/data-service';
import { useResourceBudgetSettings } from '@/hooks/use-resource-budget-settings';
import { useBudgetCalculations } from '@/hooks/useBudgetCalculations';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { FillSheet } from './fill-sheet';
import { ShiftBudgetSheet } from './shift-budget-sheet';
import { AISuggestion } from './ai-suggestion';
import type { FillType } from './fill-dialog';
import { saveMockSalesBudgetCategories } from '@/lib/mock-budget-storage';
import { getDataMode } from '@/lib/data-service';

interface EditSalesBudgetFormTMProps {
  projectId: number;
  salesBudget: SalesBudget;
  onChange: (changes: SalesBudget) => void;
  projectStartDate?: string;
  projectEndDate?: string;
  availableCategories: SalesCategory[];
  resourceBudget?: ResourceBudget; // Resource budget data to show labour breakdown
}

// Month names for columns
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Mock data: Initial budget values for sales categories (with markup over cost)
const MOCK_INITIAL_BUDGETS: Record<string, Record<string, number>> = {
  'Labour': {
    '2024-05': 35000,
    '2024-06': 35000,
    '2024-07': 35000,
    '2024-08': 35000,
    '2024-09': 35000,
    '2024-10': 35000,
    '2024-11': 35000,
    '2024-12': 35000,
    '2025-01': 35000,
  },
  'Materials': {
    '2024-05': 6500,
    '2024-06': 3900,
    '2024-07': 2600,
    '2024-08': 2600,
    '2024-09': 1950,
    '2024-10': 1950,
    '2024-11': 1300,
    '2024-12': 1300,
    '2025-01': 650,
  },
  'Equipment': {
    '2024-05': 19500,
    '2024-06': 2600,
    '2024-07': 1300,
    '2024-08': 1300,
    '2024-09': 1300,
    '2024-10': 1300,
    '2024-11': 650,
    '2024-12': 650,
    '2025-01': 650,
  },
  'Subcontractors': {
    '2024-05': 0,
    '2024-06': 0,
    '2024-07': 12000,
    '2024-08': 12000,
    '2024-09': 12000,
    '2024-10': 12000,
    '2024-11': 0,
    '2024-12': 0,
    '2025-01': 0,
  },
  'Travel & Expenses': {
    '2024-05': 1950,
    '2024-06': 1950,
    '2024-07': 1950,
    '2024-08': 1950,
    '2024-09': 1950,
    '2024-10': 1950,
    '2024-11': 1950,
    '2024-12': 1950,
    '2025-01': 1950,
  },
  'Software & Licenses': {
    '2024-05': 6500,
    '2024-06': 650,
    '2024-07': 650,
    '2024-08': 650,
    '2024-09': 650,
    '2024-10': 650,
    '2024-11': 650,
    '2024-12': 650,
    '2025-01': 650,
  },
  'Overhead': {
    '2024-05': 3900,
    '2024-06': 3900,
    '2024-07': 3900,
    '2024-08': 3900,
    '2024-09': 3900,
    '2024-10': 3900,
    '2024-11': 3900,
    '2024-12': 3900,
    '2025-01': 3900,
  },
  'Contingency': {
    '2024-05': 6500,
    '2024-06': 6500,
    '2024-07': 6500,
    '2024-08': 6500,
    '2024-09': 6500,
    '2024-10': 6500,
    '2024-11': 6500,
    '2024-12': 6500,
    '2025-01': 6500,
  },
};

// NumberInput component that handles 2dp formatting properly with max value validation
const NumberInput = ({ value, onChange, maxValue = 999999.99, onFocus, disabled, ...props }: { value: number; onChange: (value: number) => void; maxValue?: number; onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void; disabled?: boolean; [key: string]: any }) => {
  const [displayValue, setDisplayValue] = useState(value.toFixed(2));
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value.toFixed(2));
    }
  }, [value, isFocused]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (disabled) return;
    setIsFocused(true);
    // Select all text on focus for easy editing
    e.target.select();
    // Call parent's onFocus if provided
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Clear any pending debounced update
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    let numValue = parseFloat(displayValue) || 0;
    
    // Enforce maximum value
    if (numValue > maxValue) {
      numValue = maxValue;
    }
    
    const formattedValue = parseFloat(numValue.toFixed(2));
    setDisplayValue(formattedValue.toFixed(2));
    // Only call onChange if the value actually changed to avoid false "unsaved changes"
    if (formattedValue !== value) {
      onChange(formattedValue);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const val = e.target.value;
    // Allow typing numbers, decimal point, and empty string
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setDisplayValue(val);
      
      // Debounced onChange to enable toolbar buttons while typing
      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Set a new debounced timer (300ms delay to allow for slow typing)
      debounceTimerRef.current = setTimeout(() => {
        let numValue = parseFloat(val) || 0;
        // Enforce maximum value
        if (numValue > maxValue) {
          numValue = maxValue;
        }
        const formattedValue = parseFloat(numValue.toFixed(2));
        // Only call onChange if the value actually changed
        if (formattedValue !== value) {
          onChange(formattedValue);
        }
      }, 300);
    }
  };

  return (
    <Input
      {...props}
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
    />
  );
};

// Helper function to format numbers with thousand separators
const formatNumber = (value: number): string => {
  return value.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Extended category interface with monthly budgets as array for form display
interface CategoryBudgetRow extends Omit<SalesCategory, 'monthlyBudgets'> {
  monthlyBudgets: number[]; // Budget per month (array for form display, converted to/from Record for API)
}

export function EditSalesBudgetFormTM({
  projectId,
  salesBudget,
  onChange,
  projectStartDate,
  projectEndDate,
  availableCategories,
  resourceBudget,
}: EditSalesBudgetFormTMProps) {
  // Check if resource budgets are enabled at organization level
  const { settings: resourceSettings } = useResourceBudgetSettings();
  const isResourceBudgetEnabled = resourceSettings.budgetType !== 'none';
  
  // Check if this project uses resource budgets (has resource budget data)
  const projectUsesResourceBudget = resourceBudget && resourceBudget.byRole && resourceBudget.byRole.length > 0;
  
  // Labour category should be editable if:
  // 1. Organization doesn't use resource budgets (isResourceBudgetEnabled = false), OR
  // 2. Project doesn't use resource budgets (projectUsesResourceBudget = false)
  const isLabourEditable = !isResourceBudgetEnabled || !projectUsesResourceBudget;

  // Use budget calculations hook to get rates from Rate Card (respects rate hierarchy)
  const { roleBreakdown } = useBudgetCalculations(
    projectId.toString(),
    resourceBudget || null
  );

  // Create a mapping from role name to bill rate (from Rate Card)
  // This ensures we always use the latest rates from the Rate Card
  const roleRateMap = useMemo(() => {
    const map = new Map<string, number>();
    if (roleBreakdown && roleBreakdown.length > 0) {
      roleBreakdown.forEach(role => {
        map.set(role.roleName, role.averageBillRate);
      });
      console.log('[EditSalesBudgetFormTM] Role rate map created:', Array.from(map.entries()));
    }
    return map;
  }, [roleBreakdown]);
  
  // Calculate all available months for the project across all years
  const allProjectMonths = (() => {
    if (!projectStartDate || !projectEndDate) {
      // Show all months of current year if no project dates
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 12 }, (_, i) => ({ year: currentYear, month: i }));
    }
    
    const startDate = new Date(projectStartDate);
    const endDate = new Date(projectEndDate);
    
    const months: { year: number; month: number }[] = [];
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    
    while (current <= end) {
      months.push({ year: current.getFullYear(), month: current.getMonth() });
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  })();

  // Show ALL months for the entire project duration - no year filtering
  const yearMonths = allProjectMonths;

  // Create a mapping from global month index to year/month
  const monthIndexMap = yearMonths.map((ym, index) => ({
    globalIndex: index,
    year: ym.year,
    month: ym.month,
    yearMonthKey: `${ym.year}-${ym.month}` // Unique key for this month
  }));

  // Show ALL months - no dynamic loading
  const visibleMonths = monthIndexMap.map(m => m.globalIndex);
  
  // Total number of months to display
  const totalMonths = yearMonths.length;

  // Initialize categories with properly sized monthlyBudgets arrays
  const [categories, setCategories] = useState<CategoryBudgetRow[]>(() => {
    return availableCategories.map(category => {
      // Create monthly budgets array
      const monthlyBudgets = monthIndexMap.map(({ year, month }) => {
        const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`; // Format: 2024-05
        
        // First priority: Use saved monthly budgets from API if available
        if (category.monthlyBudgets && category.monthlyBudgets[monthKey] !== undefined) {
          return category.monthlyBudgets[monthKey];
        }
        
        // Second priority: Use mock initial budgets as fallback for new categories
        const categoryBudgets = MOCK_INITIAL_BUDGETS[category.name];
        return categoryBudgets?.[monthKey] || 0;
      });

      return {
        ...category,
        monthlyBudgets,
      };
    });
  });

  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [selectedCell, setSelectedCell] = useState<{
    categoryIndex: number;
    monthIndex: number | null;
  } | null>(null);
  
  // Track which Labour category is expanded to show role breakdown
  const [expandedLabourCategory, setExpandedLabourCategory] = useState<number | null>(null);

  // Save categories to localStorage for demo mode sync, and propagate to parent via onChange for API persistence
  useEffect(() => {
    const mode = getDataMode();
    if (mode === 'demo') {
      saveMockSalesBudgetCategories(projectId, categories);
    }
    // Build SalesCategory[] from CategoryBudgetRow[] and propagate via onChange
    const transformedCategories: SalesCategory[] = categories.map(c => {
      const record: Record<string, number> = {};
      c.monthlyBudgets.forEach((val, i) => {
        if (i < monthIndexMap.length) {
          const { year, month } = monthIndexMap[i];
          record[`${year}-${String(month + 1).padStart(2, '0')}`] = val;
        }
      });
      const totalBudget = c.monthlyBudgets.reduce((sum, v) => sum + v, 0);
      return {
        id: c.id,
        name: c.name,
        type: c.type,
        budget: totalBudget,
        actual: c.actual,
        remaining: totalBudget - c.actual,
        percentBilled: totalBudget > 0 ? (c.actual / totalBudget) * 100 : 0,
        scope: c.scope,
        globalCategoryId: c.globalCategoryId,
        isCustom: c.isCustom,
        monthlyBudgets: record,
      };
    });
    onChange({
      ...salesBudget,
      categories: transformedCategories,
    });
  }, [projectId, categories]);

  // Helper function to get the bill rate for a role from Rate Card
  // Falls back to the rate in resourceBudget if not found in Rate Card
  const getRoleBillRate = (roleName: string, fallbackRate: number): number => {
    const rateFromCard = roleRateMap.get(roleName);
    if (rateFromCard !== undefined) {
      return rateFromCard;
    }
    console.warn('[EditSalesBudgetFormTM] No rate found in Rate Card for role:', roleName, 'using fallback:', fallbackRate);
    return fallbackRate;
  };

  // Calculate labour revenue from resource budget (hours × bill rate per month)
  const calculateLabourRevenueFromResourceBudget = (): number[] => {
    if (!resourceBudget?.byRole) {
      return Array(totalMonths).fill(0);
    }

    const monthlyRevenue = Array(totalMonths).fill(0);
    
    // Map each project month to its calendar month and calculate revenue
    yearMonths.forEach((yearMonth, projectMonthIndex) => {
      const calendarMonth = yearMonth.month; // 0-11
      
      resourceBudget.byRole.forEach((role: RoleBudget) => {
        if (role.monthlyHours && Array.isArray(role.monthlyHours)) {
          const hours = role.monthlyHours[calendarMonth] || 0;
          const billRate = getRoleBillRate(role.role, role.billRate);
          monthlyRevenue[projectMonthIndex] += hours * billRate;
        }
      });
    });

    return monthlyRevenue;
  };

  // Get labour revenue breakdown by role for a specific project month
  const getLabourBreakdownForMonth = (projectMonthIndex: number): { role: string; hours: number; billRate: number; revenue: number }[] => {
    if (!resourceBudget?.byRole || projectMonthIndex >= yearMonths.length) {
      return [];
    }

    const calendarMonth = yearMonths[projectMonthIndex].month; // 0-11

    return resourceBudget.byRole
      .map((role: RoleBudget) => {
        const hours = role.monthlyHours?.[calendarMonth] || 0;
        const billRate = getRoleBillRate(role.role, role.billRate);
        return {
          role: role.role,
          hours,
          billRate,
          revenue: hours * billRate,
        };
      })
      .filter(item => item.hours > 0); // Only show roles with hours in this month
  };

  // Get roles that have hours allocated in any month
  const getRolesWithHours = (): RoleBudget[] => {
    if (!resourceBudget?.byRole) {
      return [];
    }

    return resourceBudget.byRole.filter((role: RoleBudget) => {
      const hasMonthlyHours = role.monthlyHours && Array.isArray(role.monthlyHours);
      const hasHours = hasMonthlyHours && role.monthlyHours!.some(hours => hours > 0);
      return hasHours;
    });
  };

  // Fill dialog state
  const [fillDialogOpen, setFillDialogOpen] = useState(false);
  const [fillSource, setFillSource] = useState<{ categoryIndex: number; monthIndex: number; value: number } | null>(null);

  // Shift budget dialog state
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftSource, setShiftSource] = useState<{ categoryIndex: number; categoryName: string } | null>(null);

  // Add Category dialog state
  const [addCategoryDialogOpen, setAddCategoryDialogOpen] = useState(false);
  const [selectedGlobalCategory, setSelectedGlobalCategory] = useState<string>('');
  const [availableGlobalCategories, setAvailableGlobalCategories] = useState<GlobalCategory[]>([]);

  // Delete confirmation state
  const [deleteAttempt, setDeleteAttempt] = useState<{ index: number; category: CategoryBudgetRow; hasActuals: boolean; hasBudget: boolean } | null>(null);

  // AI suggestion state for pattern detection
  const [patternSuggestion, setPatternSuggestion] = useState<{
    categoryIndex: number;
    value: number;
    fromMonth: number;
    toMonth: number;
  } | null>(null);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());

  // Track if this is the initial mount to prevent marking form as dirty on load
  const isInitialMount = useRef(true);

  const navigate = useNavigate();

  // Track whether any global categories exist (for empty state messaging)
  const [hasGlobalCategories, setHasGlobalCategories] = useState<boolean | null>(null);

  const goToBudgetSettings = useCallback(() => {
    navigate('/settings?tab=budget-settings');
  }, [navigate]);

  useEffect(() => {
    if (categories.length === 0) {
      fetchGlobalCategories().then(globalCategories => {
        const activeCategories = globalCategories.filter(gc => gc.status === 'active');
        setHasGlobalCategories(activeCategories.length > 0);
      }).catch(() => {
        setHasGlobalCategories(false);
      });
    }
  }, []);

  // Load global categories when Add Category dialog opens
  useEffect(() => {
    if (addCategoryDialogOpen) {
      fetchGlobalCategories().then(globalCategories => {
        // Filter out categories that are already in the project
        const existingCategoryNames = categories.map(cat => cat.name.toLowerCase());
        const available = globalCategories.filter(gc => 
          gc.status === 'active' && !existingCategoryNames.includes(gc.name.toLowerCase())
        );
        setAvailableGlobalCategories(available);
      });
    }
  }, [addCategoryDialogOpen, categories]);

  useEffect(() => {
    // Skip onChange on initial mount to prevent false "unsaved changes" alert
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Calculate totals for each category and convert monthlyBudgets array to Record<string, number>
    const updatedCategories = categories.map(cat => {
      // Convert monthlyBudgets array to Record<string, number> for API storage
      const monthlyBudgetsRecord: Record<string, number> = {};
      cat.monthlyBudgets.forEach((value, index) => {
        if (monthIndexMap[index]) {
          const { year, month } = monthIndexMap[index];
          const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`; // Format: 2024-05
          monthlyBudgetsRecord[monthKey] = value;
        }
      });

      const budget = cat.monthlyBudgets.reduce((sum, val) => sum + val, 0);
      const actual = cat.actual || 0; // Use existing actual value or 0
      const remaining = budget - actual;
      const percentBilled = budget > 0 ? (actual / budget) * 100 : 0;

      return {
        ...cat,
        budget,
        actual,
        remaining,
        percentBilled,
        monthlyBudgets: monthlyBudgetsRecord,
      };
    });

    // Calculate overall totals
    const totalBudget = updatedCategories.reduce((sum, cat) => sum + cat.budget, 0);
    const totalActual = updatedCategories.reduce((sum, cat) => sum + (cat.actual || 0), 0);
    const totalRemaining = totalBudget - totalActual;
    const totalPercentBilled = totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0;

    onChange({
      ...salesBudget,
      categories: updatedCategories,
      totals: {
        budget: totalBudget,
        actual: totalActual,
        remaining: totalRemaining,
        percentBilled: totalPercentBilled,
      },
    });
  }, [categories]);

  const updateMonthlyBudget = (categoryIndex: number, monthIndex: number, value: number) => {
    console.log('[updateMonthlyBudget] Called:', { categoryIndex, monthIndex, value, totalMonths });
    const updated = [...categories];
    console.log('[updateMonthlyBudget] Current category:', updated[categoryIndex]);
    
    // Ensure monthlyBudgets array is properly sized
    const existingBudgets = updated[categoryIndex].monthlyBudgets || [];
    console.log('[updateMonthlyBudget] Existing budgets:', existingBudgets);
    const monthlyBudgets = existingBudgets.length >= totalMonths 
      ? [...existingBudgets]
      : [...existingBudgets, ...Array(totalMonths - existingBudgets.length).fill(0)];
    console.log('[updateMonthlyBudget] Sized monthlyBudgets:', monthlyBudgets);
    
    monthlyBudgets[monthIndex] = value;
    console.log('[updateMonthlyBudget] After update:', monthlyBudgets);
    
    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets,
    };
    
    setCategories(updated);
    
    // Pattern detection for AI suggestions
    detectPattern(categoryIndex, monthIndex, value, monthlyBudgets);
  };

  // Pattern detection for AI suggestions
  const detectPattern = (categoryIndex: number, monthIndex: number, value: number, monthlyBudgets: number[]) => {
    // Don't suggest if already dismissed
    const suggestionKey = `pattern-${categoryIndex}`;
    if (dismissedSuggestions.has(suggestionKey)) return;
    
    // Check if last 3 values are the same and non-zero
    if (monthIndex >= 2 && value > 0) {
      const last3 = [
        monthlyBudgets[monthIndex - 2],
        monthlyBudgets[monthIndex - 1],
        monthlyBudgets[monthIndex]
      ];
      
      const allSame = last3.every(v => v === value);
      
      if (allSame && monthIndex < totalMonths - 1) {
        // Show suggestion to fill remaining months
        setPatternSuggestion({
          categoryIndex,
          value,
          fromMonth: monthIndex + 1,
          toMonth: totalMonths - 1
        });
      }
    }
  };

  // Row selection handlers
  const toggleRowSelection = (categoryId: number) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(categoryId)) {
      newSelection.delete(categoryId);
    } else {
      newSelection.add(categoryId);
    }
    setSelectedRows(newSelection);
    // Clear cell selection when selecting a row
    setSelectedCell(null);
  };

  const toggleAllRows = () => {
    if (selectedRows.size === categories.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(categories.map(cat => cat.id)));
    }
    // Clear cell selection when selecting all rows
    setSelectedCell(null);
  };

  // Fill handlers
  const handleFillClick = (categoryIndex: number, monthIndex: number) => {
    const category = categories[categoryIndex];
    const value = category.monthlyBudgets[monthIndex] || 0;
    setFillSource({ categoryIndex, monthIndex, value });
    setFillDialogOpen(true);
  };

  const handleFillApply = (fillType: FillType, toMonth: number) => {
    if (!fillSource) return;

    const { categoryIndex, monthIndex, value } = fillSource;
    const updated = [...categories];
    const monthlyBudgets = [...(updated[categoryIndex].monthlyBudgets || Array(totalMonths).fill(0))];

    // Apply fill based on type
    if (fillType === 'copy') {
      // Copy same value to all months in range
      for (let i = monthIndex; i <= toMonth; i++) {
        monthlyBudgets[i] = value;
      }
    } else if (fillType === 'distribute') {
      // Distribute value equally across months
      const monthCount = toMonth - monthIndex + 1;
      const valuePerMonth = value / monthCount;
      const roundedValue = Math.floor(valuePerMonth * 100) / 100;
      const remainder = value - (roundedValue * monthCount);
      
      for (let i = monthIndex; i <= toMonth; i++) {
        const isLast = i === toMonth;
        monthlyBudgets[i] = isLast ? roundedValue + remainder : roundedValue;
      }
    }

    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets,
    };

    setCategories(updated);
    setFillDialogOpen(false);
    setFillSource(null);
  };

  // Handle pattern suggestion acceptance
  const handlePatternAccept = () => {
    if (!patternSuggestion) return;
    
    const { categoryIndex, value, fromMonth, toMonth } = patternSuggestion;
    const updated = [...categories];
    const monthlyBudgets = [...(updated[categoryIndex].monthlyBudgets || Array(totalMonths).fill(0))];
    
    // Fill remaining months with the pattern value
    for (let i = fromMonth; i <= toMonth; i++) {
      monthlyBudgets[i] = value;
    }
    
    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets,
    };
    
    setCategories(updated);
    setPatternSuggestion(null);
  };
  
  const handlePatternDismiss = () => {
    if (patternSuggestion) {
      const suggestionKey = `pattern-${patternSuggestion.categoryIndex}`;
      setDismissedSuggestions(new Set(dismissedSuggestions).add(suggestionKey));
    }
    setPatternSuggestion(null);
  };

  // Duplicate handlers
  const handleDuplicateCategory = (categoryIndex: number) => {
    const sourceCategory = categories[categoryIndex];
    const newCategory: CategoryBudgetRow = {
      ...sourceCategory,
      id: Date.now() + Math.floor(Math.random() * 1000),
      name: `${sourceCategory.name} (Copy)`,
      monthlyBudgets: [...sourceCategory.monthlyBudgets],
      scope: 'project', // Duplicated categories are project-specific
      isCustom: true,
    };

    // Insert below source category
    const updated = [
      ...categories.slice(0, categoryIndex + 1),
      newCategory,
      ...categories.slice(categoryIndex + 1),
    ];

    setCategories(updated);
  };

  const handleDuplicateMultipleCategories = () => {
    if (selectedRows.size === 0) return;

    const selectedCategoryIds = Array.from(selectedRows);
    const updatedCategories = [...categories];

    // Process in reverse order to maintain correct insertion positions
    for (let i = updatedCategories.length - 1; i >= 0; i--) {
      const category = updatedCategories[i];
      if (selectedCategoryIds.includes(category.id)) {
        const newCategory: CategoryBudgetRow = {
          ...category,
          id: Date.now() + Math.floor(Math.random() * 1000) + i,
          name: `${category.name} (Copy)`,
          monthlyBudgets: [...category.monthlyBudgets],
          scope: 'project',
          isCustom: true,
        };
        updatedCategories.splice(i + 1, 0, newCategory);
      }
    }

    setCategories(updatedCategories);
    setSelectedRows(new Set());
  };

  // Shift budget handler
  const handleShiftBudget = (sourceFrom: number, sourceTo: number, destinationStart: number, clearSource: boolean) => {
    if (!shiftSource) return;

    const categoryIndex = shiftSource.categoryIndex;
    const category = categories[categoryIndex];
    const newMonthlyBudgets = [...(category.monthlyBudgets || Array(totalMonths).fill(0))];

    // Copy values to destination
    const shiftAmount = destinationStart - sourceFrom;
    const sourceMonthlyBudgets = category.monthlyBudgets || Array(totalMonths).fill(0);
    for (let i = sourceFrom; i <= sourceTo; i++) {
      const destIndex = i + shiftAmount;
      if (destIndex >= 0 && destIndex < totalMonths) {
        newMonthlyBudgets[destIndex] = sourceMonthlyBudgets[i];
      }
    }

    // Clear source if requested
    if (clearSource) {
      const destinationEnd = sourceTo + shiftAmount;
      for (let i = sourceFrom; i <= sourceTo; i++) {
        if (i < destinationStart || i > destinationEnd) {
          newMonthlyBudgets[i] = 0;
        }
      }
    }

    const updated = [...categories];
    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets: newMonthlyBudgets,
    };

    setCategories(updated);
    setShiftDialogOpen(false);
    setShiftSource(null);
  };

  // Delete handlers
  const removeCategory = (categoryIndex: number) => {
    const category = categories[categoryIndex];
    
    // Check if category has actual costs
    const hasActuals = (category.actual || 0) > 0;
    
    // Check if category has budget values
    const hasBudget = category.monthlyBudgets && category.monthlyBudgets.some(budget => budget > 0);

    // Prevent deletion if there are actual costs
    if (hasActuals) {
      setDeleteAttempt({ 
        index: categoryIndex, 
        category,
        hasActuals: true,
        hasBudget: hasBudget || false
      });
      return;
    }

    // Warn if there are budget values but allow deletion
    if (hasBudget) {
      setDeleteAttempt({ 
        index: categoryIndex, 
        category,
        hasActuals: false,
        hasBudget: true
      });
      return;
    }

    // Allow deletion of categories without costs or budget
    setCategories(categories.filter((_, i) => i !== categoryIndex));
    setSelectedRows(new Set());
    setSelectedCell(null);
  };

  const removeMultipleCategories = () => {
    if (selectedRows.size === 0) return;

    const selectedCategoryIds = Array.from(selectedRows);
    const categoriesToDelete = categories.filter(cat => selectedCategoryIds.includes(cat.id));

    // Check if any selected categories have actual costs or budget values
    const categoriesWithActuals = categoriesToDelete.filter(cat => cat.actual > 0);
    const categoriesWithBudget = categoriesToDelete.filter(cat => 
      cat.monthlyBudgets && cat.monthlyBudgets.some(budget => budget > 0)
    );

    // Prevent deletion if there are actual costs
    if (categoriesWithActuals.length > 0) {
      setDeleteAttempt({ 
        index: -1, 
        category: categoriesWithActuals[0],
        hasActuals: true,
        hasBudget: categoriesWithBudget.length > 0
      });
      return;
    }

    // Warn if there are budget values but allow deletion
    if (categoriesWithBudget.length > 0) {
      setDeleteAttempt({ 
        index: -1, 
        category: categoriesWithBudget[0],
        hasActuals: false,
        hasBudget: true
      });
      return;
    }

    // Allow deletion of categories without costs or budget
    setCategories(categories.filter(cat => !selectedCategoryIds.includes(cat.id)));
    setSelectedRows(new Set());
    setSelectedCell(null);
  };

  const cancelDeleteCategory = () => {
    setDeleteAttempt(null);
  };

  const confirmDeleteCategory = () => {
    if (!deleteAttempt) return;
    
    const selectedCategoryIds = Array.from(selectedRows);
    setCategories(categories.filter(cat => !selectedCategoryIds.includes(cat.id)));
    setSelectedRows(new Set());
    setSelectedCell(null);
    setDeleteAttempt(null);
  };

  // Add Category handler
  const handleAddCategory = () => {
    if (!selectedGlobalCategory) return;

    // Find the selected global category
    const globalCategory = availableGlobalCategories.find(gc => String(gc.id) === selectedGlobalCategory);
    if (!globalCategory) return;

    const newCategory: CategoryBudgetRow = {
      id: Date.now() + Math.floor(Math.random() * 1000),
      name: globalCategory.name,
      type: globalCategory.type,
      budget: 0,
      actual: 0,
      remaining: 0,
      percentBilled: 0,
      scope: 'project',
      isCustom: true,
      globalCategoryId: globalCategory.id,
      monthlyBudgets: Array(totalMonths).fill(0),
    };

    setCategories([...categories, newCategory]);
    setAddCategoryDialogOpen(false);
    setSelectedGlobalCategory('');
  };

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/50">
          <h2 className="text-lg font-semibold">Sales Budget by Category</h2>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => {
                if (selectedCell && selectedCell.monthIndex !== null) {
                  const { categoryIndex, monthIndex } = selectedCell;
                  const category = categories[categoryIndex];
                  const value = category.monthlyBudgets?.[monthIndex] || 0;
                  // Validation: value > 0 && monthIndex < totalMonths - 1
                  if (value > 0 && monthIndex < totalMonths - 1) {
                    handleFillClick(categoryIndex, monthIndex);
                  }
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={!selectedCell || selectedCell.monthIndex === null || (() => {
                if (!selectedCell || selectedCell.monthIndex === null) return true;
                const { categoryIndex, monthIndex } = selectedCell;
                const category = categories[categoryIndex];
                if (!category) return true;
                const value = category.monthlyBudgets?.[monthIndex] || 0;
                return value === 0 || monthIndex === totalMonths - 1;
              })()}
              title="Fill right from selected cell"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Fill
            </Button>
            <Button 
              onClick={() => {
                if (selectedRows.size === 1) {
                  const selectedCategoryId = Array.from(selectedRows)[0];
                  const categoryIndex = categories.findIndex(c => c.id === selectedCategoryId);
                  if (categoryIndex !== -1) handleDuplicateCategory(categoryIndex);
                } else if (selectedRows.size > 1) {
                  handleDuplicateMultipleCategories();
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={selectedRows.size === 0}
              title={selectedRows.size > 1 ? `Duplicate ${selectedRows.size} selected categories` : "Duplicate category"}
            >
              <Files className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
            <Button 
              onClick={() => {
                if (selectedCell && selectedCell.monthIndex !== null) {
                  const categoryIndex = selectedCell.categoryIndex;
                  setShiftSource({ 
                    categoryIndex, 
                    categoryName: categories[categoryIndex].name 
                  });
                  setShiftDialogOpen(true);
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={!selectedCell || selectedCell.monthIndex === null || (categories[selectedCell.categoryIndex]?.monthlyBudgets?.[selectedCell.monthIndex] ?? 0) === 0}
              title="Shift budget between months (select a monthly cell first)"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Shift
            </Button>
            <Button 
              onClick={() => {
                if (selectedRows.size === 1) {
                  const selectedCategoryId = Array.from(selectedRows)[0];
                  const categoryIndex = categories.findIndex(c => c.id === selectedCategoryId);
                  if (categoryIndex !== -1) {
                    removeCategory(categoryIndex);
                  }
                } else if (selectedRows.size > 1) {
                  removeMultipleCategories();
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={selectedRows.size === 0}
              title={selectedRows.size > 1 ? `Delete ${selectedRows.size} selected categories` : "Delete selected category"}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button 
              onClick={() => setAddCategoryDialogOpen(true)} 
              size="sm" 
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </div>
        </div>
        <div className="p-0">
          {categories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {hasGlobalCategories === false ? (
                <div className="space-y-2">
                  <p>No budget categories configured.</p>
                  <p>
                    Please configure categories in{' '}
                    <button
                      type="button"
                      onClick={goToBudgetSettings}
                      className="text-primary underline underline-offset-4 hover:text-primary/80 inline-flex items-center gap-1 cursor-pointer"
                    >
                      <Settings className="h-3.5 w-3.5" />
                      Settings → Budget Settings
                    </button>
                  </p>
                </div>
              ) : (
                <>No categories yet. Click "Add Category" to get started.</>
              )}
            </div>
          ) : (
            <>
              {/* 3-Column Layout: Left (sticky) + Middle (scrollable) + Right (sticky) */}
              <div className="flex border rounded-md">
                {/* LEFT SECTION: Checkbox + Category (Fixed, No Scroll) */}
                <div className="flex-shrink-0 border-r">
                  <Table className="border-collapse">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px] sticky left-0 z-10 bg-background">
                          <Checkbox
                            checked={selectedRows.size === categories.length && categories.length > 0}
                            onCheckedChange={toggleAllRows}
                            aria-label="Select all rows"
                          />
                        </TableHead>
                        <TableHead className="w-[200px] sticky left-[40px] z-10 border-r bg-background">Category</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categories.map((category, index) => {
                        const isSelected = selectedRows.has(category.id);
                        const isCellSelected = selectedCell?.categoryIndex === index;
                        const isLabourCategory = category.type === 'labour';
                        const isExpanded = expandedLabourCategory === category.id;
                        
                        return (
                          <Fragment key={category.id}>
                            <TableRow 
                              key={category.id}
                              className={cn(
                                'group',
                                isCellSelected && 'bg-primary/5',
                                isSelected && 'bg-primary/10',
                                isLabourCategory && !isLabourEditable && 'bg-muted/30'
                              )}
                            >
                              <TableCell className={cn(
                                'p-2 w-[40px] sticky left-0 z-10',
                                isCellSelected ? 'bg-primary/5' : isSelected ? 'bg-primary/10' : isLabourCategory && !isLabourEditable ? 'bg-muted/30' : 'bg-background'
                              )}>
                                <div className="flex items-center h-9">
                                  {isLabourCategory && !isLabourEditable ? (
                                    <button
                                      onClick={() => setExpandedLabourCategory(isExpanded ? null : category.id)}
                                      className="p-1 hover:bg-muted rounded"
                                      title={isExpanded ? "Collapse role breakdown" : "Expand to see role breakdown"}
                                    >
                                      {isExpanded ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                    </button>
                                  ) : (
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={() => toggleRowSelection(category.id)}
                                      aria-label={`Select ${category.name}`}
                                    />
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className={cn(
                                'p-2 w-[200px] sticky left-[40px] z-10 border-r',
                                isCellSelected ? 'bg-primary/5' : isSelected ? 'bg-primary/10' : isLabourCategory && !isLabourEditable ? 'bg-muted/30' : 'bg-background'
                              )}>
                                <div className="flex items-center gap-2 h-9">
                                  <span className={cn(
                                    "font-medium",
                                    isLabourCategory && !isLabourEditable && "text-muted-foreground"
                                  )}>
                                    {category.name}
                                  </span>
                                  {isLabourCategory && !isLabourEditable && (
                                    <span className="text-xs text-muted-foreground italic">
                                      (from Resource Budget)
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                            
                            {/* Show role names when Labour category is expanded (only if resource budgets are used) */}
                            {isLabourCategory && !isLabourEditable && isExpanded && (() => {
                              const rolesWithHours = getRolesWithHours();
                              if (rolesWithHours.length === 0) {
                                return (
                                  <TableRow className="bg-muted/10 border-l-2 border-l-primary/30">
                                    <TableCell className="p-2 w-[40px] sticky left-0 z-10 bg-muted/10"></TableCell>
                                    <TableCell className="p-2 w-[200px] sticky left-[40px] z-10 border-r bg-muted/10">
                                      <div className="flex items-center gap-2 pl-4">
                                        <span className="text-xs text-muted-foreground italic">No roles with allocated hours</span>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                );
                              }
                              return (
                                <>
                                  {rolesWithHours.map((role: RoleBudget) => (
                                    <TableRow key={`${category.id}-role-name-${role.id}`} className="bg-muted/10 border-l-2 border-l-primary/30 h-[41px]">
                                      <TableCell className="p-2 w-[40px] sticky left-0 z-10 bg-muted/10 h-[41px]"></TableCell>
                                      <TableCell className="p-2 w-[200px] sticky left-[40px] z-10 border-r bg-muted/10 h-[41px]">
                                        <Input
                                          value={`↳ ${role.role}`}
                                          readOnly
                                          className="w-full h-9 px-2 text-sm bg-muted/50 text-muted-foreground cursor-default border-muted pl-6"
                                          title={role.role}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </>
                              );
                            })()}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell className="p-2 w-[40px] sticky left-0 z-10 bg-background"></TableCell>
                        <TableCell className="p-2 w-[200px] sticky left-[40px] z-10 border-r font-semibold bg-background">Total</TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>

                {/* MIDDLE SECTION: Monthly Columns (Scrollable) */}
                <div 
                  className="flex-1 overflow-x-auto overflow-y-visible [&::-webkit-scrollbar]:h-3 [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:bg-slate-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-slate-500"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgb(203 213 225) rgb(241 245 249)',
                  }}
                >
                  <Table 
                    className="border-collapse"
                    style={{
                      // Make table width match the number of months (100px per month)
                      width: `${yearMonths.length * 100}px`,
                      minWidth: '100%'
                    }}
                  >
                    <TableHeader>
                      <TableRow>
                        {yearMonths.map((monthData) => (
                          <TableHead key={`${monthData.year}-${monthData.month}`} className="w-[100px] text-center">
                            {MONTHS[monthData.month]}/{String(monthData.year).slice(-2)}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categories.map((category, index) => {
                        const isLabourCategory = category.type === 'labour';
                        const isExpanded = expandedLabourCategory === category.id;
                        const labourRevenue = isLabourCategory && !isLabourEditable ? calculateLabourRevenueFromResourceBudget() : [];
                        
                        return (
                          <Fragment key={category.id}>
                            <TableRow key={category.id} className={cn(isLabourCategory && !isLabourEditable && 'bg-muted/30')}>
                              {visibleMonths.map((monthIndex) => {
                                // For Labour categories with resource budgets, use calculated revenue from resource budget
                                // For Labour categories without resource budgets, use manual input
                                const value = isLabourCategory && !isLabourEditable ? labourRevenue[monthIndex] || 0 : (() => {
                                  const existingBudgets = category.monthlyBudgets || [];
                                  const monthlyBudgets = Array(totalMonths).fill(0).map((_, i) => existingBudgets[i] || 0);
                                  return monthlyBudgets[monthIndex];
                                })();
                                
                                const isSelected = selectedCell?.categoryIndex === index && selectedCell?.monthIndex === monthIndex;

                                return (
                                  <TableCell key={monthIndex} className={cn("p-2 w-[100px]", isLabourCategory && !isLabourEditable && 'bg-muted/30')}>
                                    {isLabourCategory && !isLabourEditable ? (
                                      <Input
                                        value={formatCurrency(value)}
                                        readOnly
                                        disabled
                                        className="w-full h-9 px-2 text-sm text-center bg-muted/50 cursor-not-allowed text-muted-foreground"
                                        title="Labour revenue is calculated from Resource Budget (hours × bill rate)"
                                      />
                                    ) : (
                                      <NumberInput
                                        value={value}
                                        onChange={(newValue) => updateMonthlyBudget(index, monthIndex, newValue)}
                                        maxValue={999999.99}
                                        onFocus={() => setSelectedCell({ categoryIndex: index, monthIndex })}
                                        className={cn(
                                          "w-full h-9 px-2 text-sm text-center",
                                          isSelected && "ring-2 ring-ring"
                                        )}
                                      />
                                    )}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                            
                            {/* Show role breakdown when Labour category is expanded (only if resource budgets are used) */}
                            {isLabourCategory && !isLabourEditable && isExpanded && (() => {
                              const rolesWithHours = getRolesWithHours();
                              return rolesWithHours.length > 0 ? (
                                <>
                                  {rolesWithHours.map((role: RoleBudget) => {
                                    const billRate = getRoleBillRate(role.role, role.billRate);
                                    return (
                                      <TableRow key={`${category.id}-role-${role.id}`} className="bg-muted/10 border-l-2 border-l-primary/30 h-[41px]">
                                        {visibleMonths.map((projectMonthIndex) => {
                                          // Map project month index to calendar month (0-11)
                                          const calendarMonth = yearMonths[projectMonthIndex]?.month ?? 0;
                                          const hours = role.monthlyHours?.[calendarMonth] || 0;
                                          const revenue = hours * billRate;
                                          
                                          return (
                                            <TableCell key={projectMonthIndex} className="p-2 w-[100px] bg-muted/10 h-[41px]">
                                              <Input
                                                value={hours > 0 ? formatCurrency(revenue) : '-'}
                                                readOnly
                                                className="w-full h-9 px-2 text-sm text-center bg-muted/50 text-muted-foreground cursor-default border-muted"
                                                title={hours > 0 ? `${hours}h × ${formatCurrency(billRate)} = ${formatCurrency(revenue)}` : 'No hours allocated'}
                                              />
                                            </TableCell>
                                          );
                                        })}
                                      </TableRow>
                                    );
                                  })}
                                </>
                              ) : null;
                            })()}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        {visibleMonths.map((monthIndex) => {
                          const monthTotal = categories.reduce((sum, category) => {
                            const existingBudgets = category.monthlyBudgets || [];
                            const budget = existingBudgets[monthIndex] || 0;
                            return sum + budget;
                          }, 0);
                          return (
                            <TableCell key={monthIndex} className="p-2 w-[100px]">
                              <Input
                                value={formatCurrency(monthTotal)}
                                readOnly
                                className="w-full h-9 px-2 text-sm text-center font-semibold bg-muted cursor-default"
                                title={formatCurrency(monthTotal)}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>

                {/* RIGHT SECTION: Project Total (Fixed, No Scroll) */}
                <div className="flex-shrink-0 border-l-2">
                  <Table className="border-collapse">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[120px] text-center font-semibold bg-primary/10">Project Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categories.map((category) => {
                        const isLabourCategory = category.type === 'labour';
                        const isExpanded = expandedLabourCategory === category.id;
                        
                        // For Labour categories with resource budgets, calculate total from resource budget
                        // For Labour categories without resource budgets, use manual input
                        const categoryTotal = isLabourCategory && !isLabourEditable
                          ? calculateLabourRevenueFromResourceBudget().reduce((sum, val) => sum + val, 0)
                          : category.monthlyBudgets.reduce((sum, val) => sum + val, 0);
                        
                        return (
                          <Fragment key={category.id}>
                            <TableRow key={category.id} className={cn(isLabourCategory && !isLabourEditable && 'bg-muted/30')}>
                              <TableCell className={cn("p-2 w-[120px]", isLabourCategory && !isLabourEditable && 'bg-muted/30')}>
                                <Input
                                  value={formatCurrency(categoryTotal)}
                                  readOnly
                                  disabled={isLabourCategory && !isLabourEditable}
                                  className={cn(
                                    "w-full h-9 px-2 text-sm text-center font-semibold cursor-default",
                                    isLabourCategory && !isLabourEditable ? "bg-muted/50 text-muted-foreground" : "bg-primary/10"
                                  )}
                                  title={isLabourCategory && !isLabourEditable ? "Labour revenue total from Resource Budget" : formatCurrency(categoryTotal)}
                                />
                              </TableCell>
                            </TableRow>
                            
                            {/* Show role totals when Labour category is expanded (only if resource budgets are used) */}
                            {isLabourCategory && !isLabourEditable && isExpanded && (() => {
                              const rolesWithHours = getRolesWithHours();
                              return rolesWithHours.length > 0 ? (
                                <>
                                  {rolesWithHours.map((role: RoleBudget) => {
                                    const billRate = getRoleBillRate(role.role, role.billRate);
                                    const roleTotal = (role.monthlyHours || []).reduce((sum, hours, idx) => {
                                      return sum + (hours * billRate);
                                    }, 0);
                                    
                                    return (
                                      <TableRow key={`${category.id}-role-total-${role.id}`} className="bg-muted/10 border-l-2 border-l-primary/30 h-[41px]">
                                        <TableCell className="p-2 w-[120px] bg-muted/10 h-[41px]">
                                          <Input
                                            value={formatCurrency(roleTotal)}
                                            readOnly
                                            className="w-full h-9 px-2 text-sm text-center bg-muted/50 text-muted-foreground cursor-default border-muted"
                                            title={`Total revenue for ${role.role}: ${formatCurrency(roleTotal)}`}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </>
                              ) : null;
                            })()}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell className="p-2 w-[120px] bg-background">
                          <Input
                            value={formatCurrency(categories.reduce((sum, cat) => sum + cat.monthlyBudgets.reduce((s, v) => s + v, 0), 0))}
                            readOnly
                            className="w-full h-9 px-2 text-sm text-center font-bold bg-primary/20 cursor-default"
                            title={formatCurrency(categories.reduce((sum, cat) => sum + cat.monthlyBudgets.reduce((s, v) => s + v, 0), 0))}
                          />
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* AI Pattern Suggestion */}
      {patternSuggestion && (
        <AISuggestion
          type="info"
          title="Pattern Detected"
          message={`Fill remaining months with ${formatCurrency(patternSuggestion.value)}?`}
          details={`You've entered ${formatCurrency(patternSuggestion.value)} for 3 consecutive months. Would you like to fill ${MONTHS[yearMonths[patternSuggestion.fromMonth]?.month]} through ${MONTHS[yearMonths[patternSuggestion.toMonth]?.month]} with the same value?`}
          confidence={95}
          onAccept={handlePatternAccept}
          onDismiss={handlePatternDismiss}
          acceptLabel="Fill Remaining Months"
          dismissLabel="No Thanks"
        />
      )}

      {/* Fill Sheet */}
      <FillSheet
        open={fillDialogOpen}
        onOpenChange={setFillDialogOpen}
        onApply={handleFillApply}
        sourceMonth={fillSource?.monthIndex ?? 0}
        totalMonths={totalMonths}
        sourceValue={fillSource?.value ?? 0}
        direction="right"
        monthLabels={yearMonths.map((ym) => `${MONTHS[ym.month]} ${ym.year}`)}
      />

      {/* Shift Budget Sheet */}
      <ShiftBudgetSheet
        open={shiftDialogOpen}
        onOpenChange={setShiftDialogOpen}
        onShift={handleShiftBudget}
        totalMonths={totalMonths}
        monthLabels={yearMonths.map((ym) => `${MONTHS[ym.month]} ${ym.year}`)}
        roleName={shiftSource?.categoryName ?? ''}
        monthlyHours={shiftSource ? categories[shiftSource.categoryIndex]?.monthlyBudgets ?? [] : []}
        selectedMonthIndex={selectedCell?.monthIndex ?? undefined}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteAttempt !== null} onOpenChange={(open) => !open && cancelDeleteCategory()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteAttempt?.hasActuals ? 'Cannot Delete Category with Actual Costs' : 'Delete Category with Budget Values?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {deleteAttempt?.hasActuals ? (
                // Cannot delete - has actual costs
                <>
                  <p>
                    The category <strong>"{deleteAttempt?.category.name}"</strong> cannot be deleted because it has actual costs recorded.
                  </p>
                  <p className="text-sm">
                    To remove this category from your project:
                  </p>
                  <ul className="text-sm list-disc list-inside space-y-1 ml-2">
                    <li>Ensure there are no actual costs recorded</li>
                    {deleteAttempt?.hasBudget && <li>Clear all monthly budget values (set them to zero)</li>}
                    <li>Then try deleting the category again</li>
                  </ul>
                  <p className="text-sm text-muted-foreground mt-2">
                    This prevents accidental deletion of categories with actual financial data.
                  </p>
                </>
              ) : (
                // Warning - has budget values but can delete
                <>
                  <p>
                    The category <strong>"{deleteAttempt?.category.name}"</strong> has budget values allocated.
                  </p>
                  <p className="text-sm text-amber-600 dark:text-amber-500 font-medium">
                    ⚠️ Are you sure you want to delete this category?
                  </p>
                  <p className="text-sm">
                    Deleting this category will remove all budget values. This action cannot be undone.
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    If you're unsure, you can cancel and clear the budget values manually first.
                  </p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {deleteAttempt?.hasActuals ? (
              // Only "I Understand" button when there are actuals
              <AlertDialogAction onClick={cancelDeleteCategory}>
                I Understand
              </AlertDialogAction>
            ) : (
              // Cancel and Delete buttons when only budget values
              <>
                <AlertDialogCancel onClick={cancelDeleteCategory}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction onClick={confirmDeleteCategory} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete Category
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Category Dialog */}
      <Dialog open={addCategoryDialogOpen} onOpenChange={setAddCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Sales Budget Category</DialogTitle>
            <DialogDescription>
              Select a category from your Budget Settings to add to this project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="category-select">Category</Label>
              <Select value={selectedGlobalCategory} onValueChange={setSelectedGlobalCategory}>
                <SelectTrigger id="category-select">
                  <SelectValue placeholder="Select a category..." />
                </SelectTrigger>
                <SelectContent>
                  {availableGlobalCategories.length === 0 ? (
                    <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                      No categories available. All categories from Budget Settings are already added.
                    </div>
                  ) : (
                    <>
                      {/* Group by type */}
                      {availableGlobalCategories.filter(gc => gc.type === 'labour').length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Labour</div>
                          {availableGlobalCategories
                            .filter(gc => gc.type === 'labour')
                            .map(gc => (
                              <SelectItem key={gc.id} value={String(gc.id)}>
                                {gc.name}
                              </SelectItem>
                            ))}
                        </>
                      )}
                      {availableGlobalCategories.filter(gc => gc.type === 'expenses').length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Expenses</div>
                          {availableGlobalCategories
                            .filter(gc => gc.type === 'expenses')
                            .map(gc => (
                              <SelectItem key={gc.id} value={String(gc.id)}>
                                {gc.name}
                              </SelectItem>
                            ))}
                        </>
                      )}
                      {availableGlobalCategories.filter(gc => gc.type === 'subcontractors').length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Subcontractors</div>
                          {availableGlobalCategories
                            .filter(gc => gc.type === 'subcontractors')
                            .map(gc => (
                              <SelectItem key={gc.id} value={String(gc.id)}>
                                {gc.name}
                              </SelectItem>
                            ))}
                        </>
                      )}
                    </>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Categories are managed in Settings → Budget Settings
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setAddCategoryDialogOpen(false);
              setSelectedGlobalCategory('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleAddCategory} disabled={!selectedGlobalCategory}>
              Add Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
